import {
  useBulkReassignActionItemsByIdsMutation,
  useBulkUpdateStatusProjectActionItemsByIdsMutation,
  useBulkUpdateDeadlineProjectActionItemsByIdsMutation,
  useBulkAddTopicProjectActionItemsByIdsMutation,
  useDeleteActionItemMutation,
  useGetProjectActionItemsQuery,
  useProjectId,
} from '@consigli/hooks';
import { type ProjectActionItem } from '@consigli/types';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { useMsalAuth } from './use-msal-auth';

export interface UseProjectActionItemsReturn {
  // Data
  data: ProjectActionItem[] | undefined;
  // Loading states
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  // Delete functionality
  deleteModalItem: ProjectActionItem | null;
  setDeleteModalItem: (item: ProjectActionItem | null) => void;
  handleDeleteConfirm: () => Promise<void>;
  // Bulk reassign by item IDs
  handleBulkReassignItems: (
    itemIds: string[],
    target: { toMeetingUserId?: string | null; toCustomPartyName?: string | null },
  ) => Promise<void>;
  // Bulk status update by item IDs
  handleBulkUpdateStatus: (itemIds: string[], status: string) => Promise<void>;
  // Bulk deadline update by item IDs
  handleBulkUpdateDeadline: (itemIds: string[], deadline: string | null) => Promise<void>;
  // Bulk topic add by item IDs
  handleBulkAddTopic: (itemIds: string[], topic: string) => Promise<void>;
}

export const useProjectActionItems = (): UseProjectActionItemsReturn => {
  const { t } = useTranslation();
  const projectId = useProjectId();
  const { acquireToken, userEmail, userId } = useMsalAuth();
  const [token, setToken] = useState<string | null>(null);
  const [deleteModalItem, setDeleteModalItem] = useState<ProjectActionItem | null>(null);

  const [deleteActionItem] = useDeleteActionItemMutation();
  const [bulkReassignByIds] = useBulkReassignActionItemsByIdsMutation();
  const [bulkUpdateStatusByIds] = useBulkUpdateStatusProjectActionItemsByIdsMutation();
  const [bulkUpdateDeadlineByIds] = useBulkUpdateDeadlineProjectActionItemsByIdsMutation();
  const [bulkAddTopicByIds] = useBulkAddTopicProjectActionItemsByIdsMutation();

  // Acquire token on mount
  useEffect(() => {
    acquireToken()
      .then(setToken)
      .catch((error) => {
        console.error('Failed to acquire token:', error);
        toast.error(t('meeting-secretary.meetings.error-loading'));
      });
  }, [acquireToken, t]);

  const { data, isLoading, isError, error } = useGetProjectActionItemsQuery(
    {
      projectId,
      msAccessToken: token ?? '',
      userEmail,
      userId,
    },
    { skip: !token },
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteModalItem || !token) return;
    const item = deleteModalItem;
    setDeleteModalItem(null);
    try {
      await deleteActionItem({
        projectId,
        meetingId: item.meetingId,
        actionItemId: item.id,
        msAccessToken: token,
        userEmail,
        userId,
      }).unwrap();
      toast.success(t('meeting-secretary.action-items.item-deleted'));
    } catch {
      toast.error(t('meeting-secretary.meetings.error-deleting-item'));
    }
  }, [deleteModalItem, token, deleteActionItem, projectId, userEmail, userId, t]);

  // Bulk reassign items by IDs using transactional backend endpoint
  // All succeed or all fail - no partial state
  const handleBulkReassignItems = useCallback(
    async (
      itemIds: string[],
      target: { toMeetingUserId?: string | null; toCustomPartyName?: string | null },
    ) => {
      if (!token || itemIds.length === 0) return;

      try {
        await bulkReassignByIds({
          projectId,
          msAccessToken: token,
          userEmail,
          userId,
          body: {
            actionItemIds: itemIds,
            ...target,
          },
        }).unwrap();

        toast.success(
          t('meeting-secretary.action-items.items-reassigned', { count: itemIds.length }),
        );
      } catch {
        toast.error(
          t('meeting-secretary.action-items.bulk-reassign-error-no-permission', {
            defaultValue:
              'Failed to reassign items. You may not have permission to edit these meetings.',
          }),
        );
      }
    },
    [token, bulkReassignByIds, projectId, userEmail, userId, t],
  );

  // Bulk update status for items by IDs using transactional backend endpoint
  const handleBulkUpdateStatus = useCallback(
    async (itemIds: string[], status: string) => {
      if (!token || itemIds.length === 0) return;

      try {
        await bulkUpdateStatusByIds({
          projectId,
          msAccessToken: token,
          userEmail,
          userId,
          body: {
            actionItemIds: itemIds,
            status,
          },
        }).unwrap();

        toast.success(
          t('meeting-secretary.action-items.items-status-updated', { count: itemIds.length }),
        );
      } catch {
        toast.error(
          t('meeting-secretary.action-items.bulk-update-error', {
            defaultValue:
              'Failed to update items. You may not have permission to edit these meetings.',
          }),
        );
      }
    },
    [token, bulkUpdateStatusByIds, projectId, userEmail, userId, t],
  );

  // Bulk update deadline for items by IDs using transactional backend endpoint
  const handleBulkUpdateDeadline = useCallback(
    async (itemIds: string[], deadline: string | null) => {
      if (!token || itemIds.length === 0) return;

      try {
        await bulkUpdateDeadlineByIds({
          projectId,
          msAccessToken: token,
          userEmail,
          userId,
          body: {
            actionItemIds: itemIds,
            dueDate: deadline,
          },
        }).unwrap();

        toast.success(
          t('meeting-secretary.action-items.items-deadline-updated', { count: itemIds.length }),
        );
      } catch {
        toast.error(
          t('meeting-secretary.action-items.bulk-update-error', {
            defaultValue:
              'Failed to update items. You may not have permission to edit these meetings.',
          }),
        );
      }
    },
    [token, bulkUpdateDeadlineByIds, projectId, userEmail, userId, t],
  );

  // Bulk add topic for items by IDs using transactional backend endpoint
  const handleBulkAddTopic = useCallback(
    async (itemIds: string[], topic: string) => {
      if (!token || itemIds.length === 0) return;

      try {
        await bulkAddTopicByIds({
          projectId,
          msAccessToken: token,
          userEmail,
          userId,
          body: {
            actionItemIds: itemIds,
            topic,
          },
        }).unwrap();

        toast.success(
          t('meeting-secretary.action-items.items-topic-added', { count: itemIds.length }),
        );
      } catch {
        toast.error(
          t('meeting-secretary.action-items.bulk-update-error', {
            defaultValue:
              'Failed to update items. You may not have permission to edit these meetings.',
          }),
        );
      }
    },
    [token, bulkAddTopicByIds, projectId, userEmail, userId, t],
  );

  return {
    data,
    isLoading,
    isError,
    error,
    deleteModalItem,
    setDeleteModalItem,
    handleDeleteConfirm,
    handleBulkReassignItems,
    handleBulkUpdateStatus,
    handleBulkUpdateDeadline,
    handleBulkAddTopic,
  };
};
